import React, { useEffect, useState } from "react";
import { Box, VStack, Text, useColorModeValue, Flex } from "@chakra-ui/react";
import useAxios from "axios-hooks";
import { useToast } from "@chakra-ui/react";
import ChargingSessionsTable from "../components/ChargingSessionsTable";
import LoadingSpinner from "components/feedback/LoadingSpinner";

const SessionsPanel = ({ locationId }) => {
  const [ongoingSessions, setOngoingSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [ongoingLoaded, setOngoingLoaded] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");

  const ongoingUrl = `/api/chargingSession/ongoing/all`;
  const allSessionsUrl = `/api/chargingSession/forStation?stationId=${locationId}`;

  const [{ loading: ongoingLoading }, getOngoingSessions] = useAxios(
    { method: "GET", url: ongoingUrl },
    { manual: true }
  );

  const [{ loading: allSessionsLoading }, getAllSessions] = useAxios(
    { method: "GET", url: allSessionsUrl },
    { manual: true }
  );

  // Fetch ongoing sessions first
  const fetchOngoingSessions = async () => {
    try {
      const response = await getOngoingSessions();
      const data = Array.isArray(response?.data?.data) ? response?.data?.data : [];
      setOngoingSessions(data);
      setOngoingLoaded(true);
    } catch (error) {
      setOngoingSessions([]);
      setOngoingLoaded(true);
    }
  };

  // Fetch all sessions after ongoing sessions
  const fetchAllSessions = async () => {
    try {
      const response = await getAllSessions();
      
      // API returns single object in data, not an array
      let data = [];
      if (response?.data?.data) {
        if (Array.isArray(response?.data?.data)) {
          data = response?.data?.data;
        } else if (typeof response?.data?.data === 'object') {
          // Single object - wrap in array
          data = [response?.data?.data];
        }
      }
      
      setAllSessions(data);
      setAllLoaded(true);
    } catch (error) {
      setAllSessions([]);
      setAllLoaded(true);
    }
  };

  // Initial load - fetch ongoing first, then all
  useEffect(() => {
    if (locationId) {
      fetchOngoingSessions();
    }
  }, [locationId]);

  // Fetch all sessions after ongoing sessions are loaded
  useEffect(() => {
    if (ongoingLoaded && locationId) {
      fetchAllSessions();
    }
  }, [ongoingLoaded, locationId]);

  // Poll ongoing sessions every 10 seconds
  useEffect(() => {
    if (ongoingLoaded && locationId) {
      const interval = setInterval(() => {
        fetchOngoingSessions();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [ongoingLoaded, locationId]);

  if (!ongoingLoaded) {
    return (
      <Box pt={{ base: 40, md: 10 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box bg={bgColor} pt={{ base: 5, md: 5 }} px={{ base: 2, md: 4 }}>
      <VStack spacing={6} align="stretch">
        
        {/* All Charging Sessions Section */}
        <Box>
          <Flex align="center" gap={2} mb={4}>
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="#f57d36">
              Total Sessions
            </Text>
            <Text fontSize={{ base: "sm", md: "md" }} color="gray.500">
              ({allSessions.length})
            </Text>
          </Flex>
          {allLoaded ? (
            <ChargingSessionsTable sessions={allSessions} />
          ) : (
            <Box py={8}>
              <LoadingSpinner />
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default SessionsPanel;
